import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import { Layers, Plus, ChevronRight, FileText, Sparkles, X, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Epic {
  id: string;
  title: string;
  description: string | null;
  status: string;
  isSystem: boolean;
  requestCount: number;
  sortOrder: number;
}

interface ProjectInfo {
  id: string;
  name: string;
  key: string;
  orgId: string;
  orgName: string;
}

// ============================================
// New Epic Modal
// ============================================

function NewEpicModal({
  projectId,
  onClose,
  onSuccess,
}: {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post('/epics', {
        projectId,
        title,
        description: description || undefined,
      });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create epic');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            New Epic
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Epic Title
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="e.g., User Authentication"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Description <span className="text-muted font-normal">(optional)</span>
            </label>
            <textarea
              className="input w-full resize-none"
              rows={3}
              placeholder="Brief description of the epic..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !title.trim()}
            className="btn btn-primary"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Epic'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function EpicsPage() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const { setHeader, clearHeader } = usePageHeaderStore();
  const [showNewModal, setShowNewModal] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectInfo>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  const { data: epics, isLoading } = useQuery({
    queryKey: ['project-epics', projectId],
    queryFn: () => api.get<{ data: Epic[] }>(`/projects/${projectId}/epics`),
    enabled: !!projectId,
  });
  
  useEffect(() => {
    setHeader(
      'Epics',
      `${project?.name || 'Project'} - ${project?.key || ''}`,
      <Layers className="w-5 h-5" style={{ color: 'var(--primary)' }} />,
      undefined,
      {
        actions: [
          {
            id: 'new-epic',
            label: 'New Epic',
            icon: <Sparkles className="w-4 h-4" />,
            variant: 'primary',
            onClick: () => setShowNewModal(true),
          },
        ],
      }
    );
    return () => clearHeader();
  }, [project, setHeader, clearHeader]);
  
  return (
    <div className="animate-fade-in">
      {/* Epics list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-surface-200 rounded w-1/3 mb-2" />
              <div className="h-4 bg-surface-200 rounded w-1/4" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {epics?.data.map((epic) => (
            <Link
              key={epic.id}
              to={`/app/organizations/${orgId}/projects/${projectId}/epics/${epic.id}`}
              className="card flex items-center justify-between hover:border-lilac-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-lilac-50">
                  <Layers className="w-5 h-5 text-lilac-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-surface-900">{epic.title}</h3>
                  {epic.description && (
                    <p className="text-sm text-surface-500 mt-0.5 line-clamp-1">
                      {epic.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-sm text-surface-500">
                  <FileText className="w-4 h-4" />
                  {epic.requestCount}
                </div>
                {epic.isSystem && (
                  <span className="badge badge-neutral">System</span>
                )}
                <span className={`badge status-${epic.status.toLowerCase().replace('_', '-')}`}>
                  {epic.status.replace('_', ' ')}
                </span>
                <ChevronRight className="w-5 h-5 text-surface-400" />
              </div>
            </Link>
          ))}
          
          {epics?.data.length === 0 && (
            <div className="text-center py-12 card">
              <Layers className="w-12 h-12 text-surface-300 mx-auto mb-4" />
              <p className="text-surface-500">No epics yet</p>
              <button className="btn btn-primary mt-4" onClick={() => setShowNewModal(true)}>
                <Plus className="w-4 h-4" />
                Create First Epic
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* New Epic Modal */}
      {showNewModal && projectId && (
        <NewEpicModal
          projectId={projectId}
          onClose={() => setShowNewModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['project-epics', projectId] });
          }}
        />
      )}
    </div>
  );
}




